// public/v3/src/lib/push-month.js — push a patient's full month of unpushed
// activity-log entries to Elation as ONE consolidated note. Mirrors the
// legacy SPA's pushMonthlyActivitiesAlongsideBillingNote logic.
//
// Optimistic-lock contract: pushMonthForPatient threads patient._raw._updatedAt
// through every PUT body so the server's 409 conflict check (server/routes/
// patients.js:117-194) fires on stale writes. The function captures the
// response's new updatedAt after each PUT and refreshes the in-memory copy so
// the SECOND PUT (which writes the post-Elation contributor markers) carries
// the fresh timestamp. The return value includes `updatedAt` so the caller
// (push-emr orchestrator looping over patients) can refresh its own _raw cache
// for the NEXT iteration of the loop — same pattern as time-logs.jsx (Task 3).

import { ccmApi, api } from './api';
import { entryProgramFamily } from './bhi-billing';

function isPushed(entry) {
  return !!(entry && (entry.elationNoteId || entry.elationPushedAt));
}

function entryActivityType(entry) {
  const type = String((entry && (entry.type || entry.label || entry.desc)) || '').trim();
  return type || 'Other';
}

function entryActivityNote(entry) {
  const e = entry || {};
  const note = String(e.notes || e.description || e.desc || e.hint || '').trim();
  if (!note) return '';
  const type = entryActivityType(e);
  const label = String(e.label || '').trim();
  if (note === type || note === label) return '';
  return note;
}

function formatStaff(entry) {
  const staff = String((entry && (entry.staff || entry.staffName || entry.createdByName || entry.authorName)) || '').trim();
  const safeStaff = staff && !/^\(staff/i.test(staff) ? staff : 'Clinical staff';
  const credential = String(entry?.credential || '').trim();
  return credential && credential !== 'none' ? `${safeStaff}, ${credential}` : safeStaff;
}

function formatLine(entry) {
  const staff = formatStaff(entry);
  const dur = parseInt(entry.duration, 10) || parseInt(entry.mins, 10) || 0;
  const type = entryActivityType(entry);
  const activityNote = entryActivityNote(entry);
  const note = activityNote ? ' — ' + activityNote : '';
  return `- ${staff} — ${dur} min — ${type}${note}`;
}

// Push one patient's monthly rollup. Returns { ok, pushed, failed, entries,
// noteId, updatedAt }.
// patient: adapted patient object with ._raw (the full flat patient blob from API)
// monthStr: YYYY-MM
// options.alignDate (YYYY-MM-DD, optional) — overrides the auto-derived
//   alignDate. The Push-to-EMR orchestrator passes the billing note's
//   servicedAt here so the phone note lands on the same chart day, mirroring
//   v2's `pushMonthlyActivitiesAlongsideBillingNote(p.id, monthStr, pushDate)`
//   (ccm-compliance.js:7404). When omitted, behavior matches the original
//   chrome-bar bulk push.
// options.program ('ccm' | 'bhi' | undefined) — filters contributor entries
//   by entryProgramFamily so a CCM push only consolidates CCM-tagged
//   activity and a BHI push only consolidates BHI-tagged activity. The two
//   programs produce separate phone notes in Elation with distinct titles
//   ("CCM monthly time summary" vs "BHI monthly time summary"). Omitted
//   defaults to 'ccm' for backwards compatibility with the existing
//   per-patient push orchestrator.
//
// The returned `updatedAt` is the server's post-PUT timestamp from the SECOND
// (final) PUT — callers iterating over patients should reseed their
// `patient._raw._updatedAt` with this value before the next loop iteration,
// otherwise the next operation on the same patient will 409.
export async function pushMonthForPatient(patient, monthStr, options) {
  const opts = options || {};
  const program = opts.program === 'bhi' ? 'bhi' : 'ccm';
  const titlePrefix = program === 'bhi' ? 'BHI' : 'CCM';
  const raw = patient?._raw || patient || {};
  // Pull the cached optimistic-lock timestamp once. Both PUTs send it; the
  // server returns a fresh one on each successful write and we propagate the
  // most recent value to the second PUT (and on out to the caller).
  let currentUpdatedAt = raw._updatedAt || null;
  if (!raw.elation_id) {
    return { ok: false, error: 'not linked to Elation' };
  }
  const activityLog = Array.isArray(raw.activityLog) ? raw.activityLog.slice() : [];
  const contributorIndices = [];
  activityLog.forEach((entry, idx) => {
    if (!entry || isPushed(entry)) return;
    if (entry.isConsolidatedNote) return;
    if (!entry.date || !entry.date.startsWith(monthStr)) return;
    if (entryProgramFamily(entry) !== program) return;
    contributorIndices.push(idx);
  });
  if (contributorIndices.length === 0) {
    return { ok: true, pushed: 0, failed: 0, entries: 0, noteId: null };
  }

  const totalMin = contributorIndices.reduce((sum, idx) => {
    const e = activityLog[idx];
    return sum + (parseInt(e.duration, 10) || parseInt(e.mins, 10) || 0);
  }, 0);

  const lines = [
    `${titlePrefix} monthly time summary`,
    `Total time: ${totalMin} minutes`,
    '',
    ...contributorIndices.map(idx => formatLine(activityLog[idx])),
  ];

  // Anchor the consolidated note to the first of the month (or the last entry's
  // date if the user is pushing mid-month). Match the legacy pattern: use the
  // last contributor entry's date as alignDate. The Push-to-EMR orchestrator
  // can override this via opts.alignDate so the phone note matches the billing
  // note's servicedAt — critical for the chart audit to pair the two notes.
  let alignDate;
  if (opts.alignDate && /^\d{4}-\d{2}-\d{2}$/.test(opts.alignDate)) {
    alignDate = opts.alignDate;
  } else {
    const lastContrib = activityLog[contributorIndices[contributorIndices.length - 1]];
    alignDate = (lastContrib && lastContrib.date) || `${monthStr}-01`;
  }

  // The synthetic entry's `program` field uses the patient's program slot
  // for the active program (BHI: billing.bhiProgram; CCM: billing.program).
  // Falls back to a canonical CPT when the slot isn't set so
  // entryProgramFamily classifies the synthetic correctly. The synthetic
  // carries `isConsolidatedNote: true` so it's excluded from future
  // billing-note time totals regardless of program.
  const syntheticProgram = program === 'bhi'
    ? (raw.billing?.bhiProgram || '99484')
    : (raw.billing?.program || '');
  const syntheticEntry = {
    id: 'syn-' + Date.now(),
    date: alignDate,
    duration: totalMin,
    program: syntheticProgram,
    type: `${titlePrefix} monthly summary`,
    staff: '(v3 batch push)',
    notes: lines.join('\n'),
    isConsolidatedNote: true,
    consolidatedFromIndices: contributorIndices.slice(),
  };

  const syntheticIdx = activityLog.length;
  const updatedActivityLog = [...activityLog, syntheticEntry];
  // Body includes _updatedAt so the server's optimistic-lock check fires on
  // stale writes; the server strips _updatedAt before storing.
  const patientWithSynthetic = { ...raw, activityLog: updatedActivityLog, _updatedAt: currentUpdatedAt };

  // Persist the synthetic entry first. Capture the response's new updatedAt
  // so the second PUT below carries the fresh timestamp.
  const resp1 = await ccmApi.updatePatient(patient.id, patientWithSynthetic);
  if (resp1 && resp1.updatedAt) currentUpdatedAt = resp1.updatedAt;

  // Push the synthetic
  let noteId = null;
  let pushedAt = null;
  try {
    const res = await api.post('/data/elation/push-activity-log', {
      patientId: patient.id,
      activityIndex: syntheticIdx,
    });
    noteId = (res && res.noteId) || 'created';
    pushedAt = (res && res.pushedAt) || new Date().toISOString();
    if (res && res.updatedAt) currentUpdatedAt = res.updatedAt;
  } catch (e) {
    return { ok: false, error: e?.message || 'push failed', entries: contributorIndices.length };
  }

  // Mark contributors as pushed with the same noteId
  const finalActivityLog = updatedActivityLog.map((entry, idx) => {
    if (contributorIndices.includes(idx)) {
      return {
        ...entry,
        elationNoteId: noteId,
        elationPushedAt: pushedAt,
        elationNoteType: 'consolidated_monthly',
      };
    }
    return entry;
  });
  // Update synthetic too (server already set fields, but in case the response was minimal)
  finalActivityLog[syntheticIdx] = {
    ...finalActivityLog[syntheticIdx],
    elationNoteId: noteId,
    elationPushedAt: pushedAt,
    elationNoteType: 'activity_log',
  };

  const resp2 = await ccmApi.updatePatient(patient.id, { ...raw, activityLog: finalActivityLog, _updatedAt: currentUpdatedAt });
  if (resp2 && resp2.updatedAt) currentUpdatedAt = resp2.updatedAt;

  return { ok: true, pushed: 1, failed: 0, entries: contributorIndices.length, noteId, updatedAt: currentUpdatedAt };
}
